import { useCallback, useEffect, useRef, useState } from 'react';

import { NOTIFICATION_DEFAULT_ERROR_MESSAGE, useNotificationContext } from 'application/providers/NotificationProvider';

import { PromoSelectionDraftAction, useMediaPromoSelectionDraftMutation } from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

import type { PromoListItem } from './types';
import type { PromoSelectionSync, UseOptimisticPromoSelectionOptions } from './useOptimisticPromoSelection.types';
import { getPromoError } from './utils';

const useOptimisticPromoSelection = ({
  companyId,
  draftId,
  isBulkSelectionActionPending,
  isInitialLoading,
  applyPromoSelection,
  setSelectedCount,
}: UseOptimisticPromoSelectionOptions) => {
  const notification = useNotificationContext();
  const isMountedRef = useRef(true);
  const currentDraftIdRef = useRef(draftId);
  const selectionsRef = useRef(new Map<string, PromoSelectionSync>());
  const [pendingPromoIds, setPendingPromoIds] = useState<Set<string>>(new Set());
  const [updateDraft] = useMediaPromoSelectionDraftMutation({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    currentDraftIdRef.current = draftId;
    selectionsRef.current.clear();
    setPendingPromoIds(new Set());
  }, [draftId]);

  const setPendingPromo = useCallback((promoId: string, isPending: boolean) => {
    setPendingPromoIds((currentIds) => {
      const nextIds = new Set(currentIds);

      if (isPending) {
        nextIds.add(promoId);
      } else {
        nextIds.delete(promoId);
      }

      return nextIds;
    });
  }, []);

  const updateSelectedCount = useCallback(
    (currentIsSelected: boolean, nextIsSelected: boolean) => {
      if (currentIsSelected === nextIsSelected) {
        return;
      }

      setSelectedCount((currentCount) => Math.max(0, currentCount + (nextIsSelected ? 1 : -1)));
    },
    [setSelectedCount]
  );

  const synchronizePromo = useCallback(
    (promoId: string, draftId: string, requestCompanyId: string): Promise<void> => {
      const initialSelection = selectionsRef.current.get(promoId);

      if (!initialSelection || initialSelection.isSynchronizing) {
        return Promise.resolve();
      }

      initialSelection.isSynchronizing = true;

      const synchronizeNext = (): Promise<void> => {
        const selection = selectionsRef.current.get(promoId);

        if (!selection || !isMountedRef.current || currentDraftIdRef.current !== draftId) {
          return Promise.resolve();
        }

        const requestedIsSelected = selection.desiredIsSelected;
        return updateDraft({
          variables: {
            input: {
              action: requestedIsSelected ? PromoSelectionDraftAction.Select : PromoSelectionDraftAction.Deselect,
              companyId: requestCompanyId,
              promoIds: [Number(promoId)],
              promoSelectionDraftId: draftId,
            },
          },
        })
          .then((result) => {
            const draftResult = result.data?.result;

            if (!draftResult?.ok) {
              throw getPromoError(draftResult?.errors?.[0]?.messages[0]);
            }

            if (isMountedRef.current && currentDraftIdRef.current === draftId) {
              const currentSelection = selectionsRef.current.get(promoId);

              if (currentSelection) {
                currentSelection.confirmedIsSelected = requestedIsSelected;
              }
            }
          })
          .catch((error) => {
            if (!isMountedRef.current || currentDraftIdRef.current !== draftId) {
              return;
            }

            const currentSelection = selectionsRef.current.get(promoId);

            if (!currentSelection || currentSelection.desiredIsSelected !== requestedIsSelected) {
              return;
            }

            const rollbackIsSelected = currentSelection.confirmedIsSelected;

            currentSelection.desiredIsSelected = rollbackIsSelected;
            applyPromoSelection(promoId, rollbackIsSelected);
            updateSelectedCount(requestedIsSelected, rollbackIsSelected);
            notification.showErrorNotification({
              children: error instanceof Error ? error.message : NOTIFICATION_DEFAULT_ERROR_MESSAGE,
            });
          })
          .then(() => {
            if (!isMountedRef.current || currentDraftIdRef.current !== draftId) {
              return undefined;
            }

            const currentSelection = selectionsRef.current.get(promoId);

            if (!currentSelection) {
              return undefined;
            }

            if (currentSelection.desiredIsSelected === currentSelection.confirmedIsSelected) {
              selectionsRef.current.delete(promoId);

              return undefined;
            }

            return synchronizeNext();
          });
      };

      return synchronizeNext().finally(() => {
        const currentSelection = selectionsRef.current.get(promoId);

        if (currentSelection) {
          currentSelection.isSynchronizing = false;
        }

        if (isMountedRef.current && currentDraftIdRef.current === draftId) {
          setPendingPromo(promoId, false);
        }
      });
    },
    [applyPromoSelection, notification, setPendingPromo, updateDraft, updateSelectedCount]
  );

  const onPromoSelectionChange = useCallback(
    (promo: PromoListItem, isSelected: boolean) => {
      const promoId = Number(promo.id);

      if (!companyId || !draftId || isInitialLoading || isBulkSelectionActionPending || !Number.isInteger(promoId)) {
        return;
      }

      const currentSelection = selectionsRef.current.get(promo.id);
      const currentIsSelected = currentSelection?.desiredIsSelected ?? promo.isSelected;

      if (currentIsSelected === isSelected) {
        return;
      }

      const nextSelection = currentSelection || {
        confirmedIsSelected: promo.isSelected,
        desiredIsSelected: promo.isSelected,
        isSynchronizing: false,
      };

      nextSelection.desiredIsSelected = isSelected;
      selectionsRef.current.set(promo.id, nextSelection);
      applyPromoSelection(promo.id, isSelected);
      updateSelectedCount(currentIsSelected, isSelected);

      if (!nextSelection.isSynchronizing) {
        setPendingPromo(promo.id, true);
        synchronizePromo(promo.id, draftId, companyId).catch(() => undefined);
      }
    },
    [
      applyPromoSelection,
      companyId,
      draftId,
      isBulkSelectionActionPending,
      isInitialLoading,
      setPendingPromo,
      synchronizePromo,
      updateSelectedCount,
    ]
  );

  return {
    hasPendingPromoSelections: pendingPromoIds.size > 0,
    onPromoSelectionChange,
  };
};

export default useOptimisticPromoSelection;
